import React, {useEffect, useState, useMemo} from "react";
import {ProductBox} from "./ProductBox";
import {useSelector} from "react-redux";
import Pagination from "react-js-pagination"
import {Formik} from "formik";
import { getSupplierProductsNameBrand } from '../../CommonApplication/SupplierSelection/SupplierSelectionCrud'
import { fetchBrands } from '../ordersCrud'
import FormControl from "@material-ui/core/FormControl";
import {Select, ListSubheader, TextField, InputAdornment} from "@material-ui/core";
import FilledInput from "@material-ui/core/FilledInput";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import SearchIcon from "@material-ui/icons/Search";
import Switch from "@material-ui/core/Switch";

const AVAILABILITY_FILTER_LOCKED_ROLES = ['buyer_approver', 'buyer_creator'];

const isAvailabilityFilterLocked = (user) =>
    user?.roles?.some((role) => AVAILABILITY_FILTER_LOCKED_ROLES.includes(role));

const getDefaultIsQohAvailable = (supplier, user) => {
    if (supplier?.isExternal === true) return false;
    if (isAvailabilityFilterLocked(user)) return true;
    return (
        process.env.REACT_APP_MCS_TENANT === user.tenant ||
        user.roles.includes('buyer_store') ||
        user.roles.includes('buyer_external') ||
        user.roles.includes('buyer_store_admin')
    );
};

export function ProductsList({filterCategory,supplier,categories,company,locations,isOrderSummary = false, setIsCartUpdated, isCartUpdated,
                                orderId, currentOrders, setCurrentOrders, defaultInvoiceDate, defaultShipDate , order, user}){
    const {accessToken} = useSelector(
        ({auth}) => ({
            accessToken: auth.authToken,
        })
    )

    // console.log("Locations: ", locations)
    // console.log("Company: ", company)

    const initialPaginationOptions = {
        totalSize: 0,
        sizePerPage: 12,
        page: 1
    }

    const [products,setProducts] = useState([]);
    const [paginationOptions,setPaginationOptions] = useState(initialPaginationOptions)
    const [searchName,setSearchName] = useState("")
    const [searchBrand, setSearchBrand] = useState("All")
    const [searchCategory, setSearchCategory] = useState({})
    const [filteredCategoryList, setFilteredCategoryList] = useState([])
    const [showSnackBar, setShowSnackBar] = useState(false);
    const [categorySearchText, setCategorySearchText] = useState("");
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [brandSearchText, setBrandSearchText] = useState("");
    const [availableBrands, setAvailableBrands] = useState([]);
    const availabilityFilterLocked = isAvailabilityFilterLocked(user);
    const [isQohAvailable, setIsQohAvailable] = useState(() =>
        getDefaultIsQohAvailable(supplier, user)
    );
    const effectiveIsQohAvailable = availabilityFilterLocked ? true : isQohAvailable;

    // Helper function to check if text contains search term
    const containsText = (text, searchText) => {
        if(text){
            return text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
        }
        return false;
    }

    // Helper function to flatten categories for search
    const flattenCategories = (categories, result = []) => {
        categories.forEach(category => {
            result.push(category);
            if (category.children && category.children.length > 0) {
                flattenCategories(category.children, result);
            }
        });
        return result;
    }

    // Get displayed categories based on search
    const displayedCategories = useMemo(() => {
        if (!categorySearchText) {
            return flattenCategories(categories);
        }
        return flattenCategories(categories).filter((category) =>
            containsText(category.name, categorySearchText)
        );
    }, [categorySearchText, categories]);

    // Get unique brands from available brands and filter based on search
    const displayedBrands = useMemo(() => {
        if (!brandSearchText) {
            return availableBrands;
        }
        return availableBrands.filter((brand) =>
            containsText(brand.name, brandSearchText)
        );
    }, [brandSearchText, availableBrands]);

    // Helper function to get category display text with hierarchy
    const getCategoryDisplayText = (category) => {
        if (!category.parent) {
            return category.name;
        }
        
        // Build the full path by traversing up the parent chain
        const buildPath = (cat) => {
            if (!cat.parent) {
                return cat.name;
            }
            
            // Find the parent category in the flattened categories
            const allCategories = flattenCategories(categories);
            const parentCategory = allCategories.find(c => c._id === cat.parent);
            
            if (parentCategory) {
                return buildPath(parentCategory) + " > " + cat.name;
            }
            
            return cat.name;
        };
        
        return buildPath(category);
    };

    // Handle category selection
    const handleCategorySelect = (categoryId) => {
        if (!categoryId) {
            // "All Categories" selected - keep brand filter if active
            setSelectedCategoryId("");
            setSearchCategory({});
            // setSearchBrand("All");
            setSearchName("");

            getSupplierProductsNameBrand(supplier._id, accessToken, paginationOptions.page, paginationOptions.sizePerPage, "", false, {}, searchBrand, company._id, effectiveIsQohAvailable).then(response => {
                setProducts(response.data.products);
                setPaginationOptions({
                    page: paginationOptions.page,
                    totalSize: response.data.totalCount,
                    sizePerPage: paginationOptions.sizePerPage
                });
            });
            return;
        }
        
        const selectedCategory = flattenCategories(categories).find(cat => cat._id === categoryId);
        setSelectedCategoryId(categoryId);
        setSearchCategory(selectedCategory || {});
        // setSearchBrand("");
        setSearchName("");

        getSupplierProductsNameBrand(supplier._id, accessToken, paginationOptions.page, paginationOptions.sizePerPage, "", true, selectedCategory || {}, searchBrand, company._id, effectiveIsQohAvailable).then(response => {
            setProducts(response.data.products);
            setPaginationOptions({
                page: paginationOptions.page,
                totalSize: response.data.totalCount,
                sizePerPage: paginationOptions.sizePerPage
            });
        });
    };

    // Handle brand selection
    const handleBrandSelect = (brand) => {
        if (!brand || brand === "All") {
            // "All Brands" selected - keep category filter if active
            setSearchBrand("All");
            setSearchName("");

            // Pass current category filter
            const applyCategoryFilter = selectedCategoryId !== "";
            getSupplierProductsNameBrand(supplier._id, accessToken, paginationOptions.page, paginationOptions.sizePerPage, "", applyCategoryFilter, searchCategory, "All", company._id, effectiveIsQohAvailable).then(response => {
                setProducts(response.data.products);
                setPaginationOptions({
                    page: paginationOptions.page,
                    totalSize: response.data.totalCount,
                    sizePerPage: paginationOptions.sizePerPage
                });
            });
            return;
        }

        // Keep category filter when selecting brand
        setSearchBrand(brand);
        setSearchName("");

        const applyCategoryFilter = selectedCategoryId !== "";
        getSupplierProductsNameBrand(supplier._id, accessToken, paginationOptions.page, paginationOptions.sizePerPage, "", applyCategoryFilter, searchCategory, brand, company._id, effectiveIsQohAvailable).then(response => {
            setProducts(response.data.products);
            setPaginationOptions({
                page: paginationOptions.page,
                totalSize: response.data.totalCount,
                sizePerPage: paginationOptions.sizePerPage
            });
        });
    };

    useEffect(() => {
        setIsQohAvailable(getDefaultIsQohAvailable(supplier, user));
    }, [supplier, user.tenant, user.roles]);

    useEffect(() => {

        getSupplierProductsNameBrand(supplier._id,accessToken,paginationOptions.page,paginationOptions.sizePerPage,searchName, searchBrand === "" ? true : false, filterCategory, searchBrand, company._id, effectiveIsQohAvailable).then(response=>{

            setProducts(response.data.products)
            setPaginationOptions({
                page:paginationOptions.page,
                totalSize: response.data.totalCount,
                sizePerPage: paginationOptions.sizePerPage
            })
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[company])

    // Fetch all brands on component mount (only for GFR tenant)
    useEffect(() => {
        if (user.tenant == process.env.REACT_APP_GFR_TENANT) {
            // Fetch brands from dedicated endpoint
            fetchBrands(supplier._id, accessToken).then(response => {
                // Store full brand objects and sort by name
                const brands = response.data.sort((a, b) => a.name.localeCompare(b.name));
                console.log("Fetched brands:", brands);
                setAvailableBrands(brands);
            }).catch(error => {
                console.error("Error fetching brands:", error);
            });
        }
    }, [supplier._id, accessToken])

    const handlePageChange = (pageNumber) =>{
        getSupplierProductsNameBrand(supplier._id,accessToken,pageNumber,paginationOptions.sizePerPage,searchName, searchBrand === "" ? true : false, searchCategory, searchBrand, company._id, effectiveIsQohAvailable).then(response=>{
            setProducts(response.data.products)
            setPaginationOptions({
                page:pageNumber,
                totalSize: response.data.totalCount,
                sizePerPage: paginationOptions.sizePerPage
            })
        })
    }

    const handlePageSizeChange = (pageSize) =>{
        console.log("Element: ", pageSize)
        getSupplierProductsNameBrand(supplier._id,accessToken,paginationOptions.page,pageSize,searchName, searchBrand === "" ? true : false, searchCategory, searchBrand, company._id, effectiveIsQohAvailable).then(response=>{
            setProducts(response.data.products)
            setPaginationOptions({
                page:paginationOptions.page,
                totalSize: response.data.totalCount,
                sizePerPage: pageSize
            })
        })
    }

    const handleBrandClick = (brand) =>{
        if(brand === 'All'){
            setFilteredCategoryList([])
        }
        // setSearchCategory({})
        setSearchBrand(brand)
        setSearchName("")
        const applyCategoryFilter = searchCategory && Object.keys(searchCategory).length > 0;
        getSupplierProductsNameBrand(supplier._id,accessToken,paginationOptions.page,paginationOptions.sizePerPage,"", applyCategoryFilter, searchCategory, brand, company._id, effectiveIsQohAvailable).then(response=>{
            setProducts(response.data.products)
            setPaginationOptions({
                page:paginationOptions.page,
                totalSize: response.data.totalCount,
                sizePerPage: paginationOptions.sizePerPage
            })
        })
    }

    const handleCategoryClick = (category, catLevel) =>{
        setSearchCategory(category)
        // setSearchBrand("")
        setSearchName("")

        setFilteredCategoryList([...filteredCategoryList.slice(0, catLevel), category.name])

        getSupplierProductsNameBrand(supplier._id,accessToken,paginationOptions.page,paginationOptions.sizePerPage,"", true, category, searchBrand, company._id, effectiveIsQohAvailable).then(response=>{
            setProducts(response.data.products)
            setPaginationOptions({
                page:paginationOptions.page,
                totalSize: response.data.totalCount,
                sizePerPage: paginationOptions.sizePerPage
            })
        })
    }

    const getCategoryBrandList = (categories, listItems = [], categoryLevel = 0) => {
        
        categories.forEach((category, index) => {
            const isSelected = searchCategory && searchCategory._id === category._id;
            const isTopLevel = category.parent === null;
            const hasChildren = category.children.length > 0;
            const shouldShowChildren = filteredCategoryList.length > 0 && filteredCategoryList.includes(category.name);
            
            // Generate unique key for this category
            const uniqueKey = `${categoryLevel}-${index}-${category.name}`;
            
            // Determine styling based on category type
            const listItemStyle = {
                listStyleType: 'none',
                paddingLeft: isTopLevel ? 0 : categoryLevel * 15,
                margin: 0
            };
            
            // Determine text styling
            const textClassName = isSelected 
                ? "text-danger font-weight-bolder text-hover-primary mb-1 font-size-lg"
                : "text-dark-75 font-weight-bolder text-hover-primary mb-1 font-size-lg";
            
            // Add padding for non-top-level items
            const finalClassName = isTopLevel ? textClassName : `${textClassName} pl-2`;
            
            // Create category element
            const categoryElement = (
                <span 
                    onClick={() => handleCategoryClick(category, categoryLevel)}
                    className={finalClassName}
                    style={{ cursor: 'pointer' }}>
                    {category.name}
                </span>
            );
            
            // Render the list item
            if (hasChildren && isTopLevel) {
                // Top-level categories with children
                listItems.push(
                    <li key={uniqueKey} style={listItemStyle}>
                        {categoryElement}
                    </li>
                );
            } else {
                // Categories with children (non-top-level) or leaf categories
                listItems.push(
                    <li key={uniqueKey} style={listItemStyle}>
                        <div className="d-flex align-items-center mb-1">
                            <span className="bullet bullet-bar bg-success align-self-stretch"></span>
                            {categoryElement}
                        </div>
                    </li>
                );
            }
            
            // Recursively process children if they should be shown
            if (hasChildren && shouldShowChildren) {
                listItems = getCategoryBrandList(category.children, listItems, categoryLevel + 1);
            }
        });
        
        return listItems;
    }

    const handleIsQohAvailableChange = (checked) => {
        if (availabilityFilterLocked) return;
        setIsQohAvailable(checked);
        getSupplierProductsNameBrand(supplier._id,accessToken,paginationOptions.page,paginationOptions.sizePerPage,searchName, searchBrand === "" ? true : false, searchCategory, searchBrand, company._id, checked).then(response=>{
            setProducts(response.data.products)
            setPaginationOptions({
                page:paginationOptions.page,
                totalSize: response.data.totalCount,
                sizePerPage: paginationOptions.sizePerPage
            })
        })
    }

    return(
       
        <div className="row">
        <div className="col-lg-2 col-xxl-2">
            {/* <ul>
                {supplier.supplierProductBrands.length > 0 ?
                    supplier.supplierProductBrands.map(brand => 
                        <li style={{ 'list-style-type':'none', padding:0, margin:0}}>
                            <a 
                            onClick={() => handleBrandClick(brand.brand)}
                            className="text-dark text-hover-primary mb-1 font-size-lg">
                                {brand.brand}
                            </a>
                        </li>
                        
                    )
                    :
                    null
                }
            </ul> */}
            <ul>
                {categories.length > 0 ?
                    <div>
                        <li key={"all"} style={{ 'listStyleType':'none', paddingLeft:0, margin:0}}>
                            {
                                searchBrand === 'All' ?
                                    <span 
                                        onClick={() => handleBrandClick("All")}
                                        style={{ cursor: 'pointer' }}
                                        className="text-danger font-weight-bolder text-hover-primary mb-1 font-size-lg">
                                            {"All"}
                                    </span>
                                    :
                                    <span 
                                        onClick={() => handleBrandClick("All")}
                                        style={{ cursor: 'pointer' }}
                                        className="text-dark font-weight-bolder text-hover-primary mb-1 font-size-lg">
                                            {"All"}
                                    </span>
                            }
                            
                        </li>
                        {getCategoryBrandList(categories)}
                    </div>
                    // null
                    :
                    null
                }
            </ul>
        </div>
        <div className="col-lg-9 col-xxl-9">
            <div className="row">
                {!(supplier && supplier.isExternal === true) && !availabilityFilterLocked && (
                    <div className="col-lg-2">
                        <div className="d-flex align-items-center">
                            <label htmlFor="isQohAvailable" className="mr-3 mb-0">Show Available Products Only</label>
                            <Switch
                                id="isQohAvailable"
                                checked={isQohAvailable}
                                onChange={(e) => handleIsQohAvailableChange(e.target.checked)}
                                name="isQohAvailable"
                                color="primary"
                            />
                        </div>
                    </div>
                )}
                <div className={user.tenant == process.env.REACT_APP_GFR_TENANT ? "col-lg-3" : "col-lg-4"}>
                    <FormControl variant="filled" style={{ width: "100%" }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            MenuProps={{ autoFocus: false }}
                            value={selectedCategoryId}
                            style={{ minWidth: "200px" }}
                            input={<FilledInput 
                                name="category" 
                                id="category"
                                onChange={(element) => {
                                    handleCategorySelect(element.target.value);
                                }}
                                onClose={() => setCategorySearchText("")}
                                type="text"
                                className="form-control form-control-solid h-auto" 
                            />}
                        >
                            <ListSubheader>
                                <TextField
                                    size="small"
                                    autoFocus
                                    placeholder="Type to search categories..."
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        )
                                    }}
                                    onChange={(e) => setCategorySearchText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key !== "Escape") {
                                            e.stopPropagation();
                                        }
                                    }}
                                />
                            </ListSubheader>
                            <MenuItem value="">
                                <em>All Categories</em>
                            </MenuItem>
                            {displayedCategories.map((category) => (
                                <MenuItem key={category._id} value={category._id}>
                                    {getCategoryDisplayText(category)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                {/* Brand dropdown - only show for GFR tenant */}
                {user.tenant == process.env.REACT_APP_GFR_TENANT && (
                    <div className="col-lg-3">
                        <FormControl variant="filled" style={{ width: "100%" }}>
                            <InputLabel>Brand</InputLabel>
                            <Select
                                MenuProps={{ autoFocus: false }}
                                value={searchBrand}
                                style={{ minWidth: "200px" }}
                                input={<FilledInput
                                    name="brand"
                                    id="brand"
                                    onChange={(element) => {
                                        handleBrandSelect(element.target.value);
                                    }}
                                    onClose={() => setBrandSearchText("")}
                                    type="text"
                                    className="form-control form-control-solid h-auto"
                                />}
                            >
                                <ListSubheader>
                                    <TextField
                                        size="small"
                                        autoFocus
                                        placeholder="Type to search brands..."
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            )
                                        }}
                                        onChange={(e) => setBrandSearchText(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key !== "Escape") {
                                                e.stopPropagation();
                                            }
                                        }}
                                    />
                                </ListSubheader>
                                <MenuItem value="All">
                                    <em>All Brands</em>
                                </MenuItem>
                                {displayedBrands.map((brand) => (
                                    <MenuItem key={brand._id} value={brand._id}>
                                        {brand.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                )}
                <div className={user.tenant == process.env.REACT_APP_GFR_TENANT ? "col-lg-4" : "col-lg-6"}>
                    <div className="row justify-content-between mb-4">
                        <Formik
                            initialValues={{searchText:""}}
                            onSubmit={(values) =>{
                                setSearchName(values.searchText)
                                getSupplierProductsNameBrand(supplier._id,accessToken,1,paginationOptions.sizePerPage,values.searchText, searchBrand === "" ? true : false, searchCategory, searchBrand, company._id, effectiveIsQohAvailable ).then(response=>{
                                    console.log('Result: response.data.products', response.data.products)
                                    setProducts(response.data.products)
                                    setPaginationOptions({
                                        page:1,
                                        totalSize: response.data.totalCount,
                                        sizePerPage: paginationOptions.sizePerPage
                                    })
                                })

                            }}>
                            {({values, handleSubmit, handleBlur, handleChange, setFieldValue})=>(
                                <form onSubmit={handleSubmit} className="form form-label-right w-100">
                                    <div className="form-group row">
                                        <div className="col-lg-8">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="searchText"
                                                placeholder="Search"
                                                onBlur={handleBlur}
                                                value={values.searchText}
                                                onChange={(e) =>{
                                                    setFieldValue("searchText",e.target.value);
                                                    handleSubmit();
                                                }}
                                            />
                                            <small className="form-text text-muted">
                                                <b>Search</b> by name or item id
                                            </small>
                                        </div>
                                        <button
                                            id="addLocationButton"
                                            type="button"
                                            onClick={() => {
                                                setFieldValue("searchText",'')
                                                setSelectedCategoryId("")
                                                setSearchCategory({})
                                                setSearchBrand("All")
                                                getSupplierProductsNameBrand(supplier._id, accessToken, 1, paginationOptions.sizePerPage, "", false, {}, "All", company._id, effectiveIsQohAvailable).then(response => {
                                                    setProducts(response.data.products);
                                                    setPaginationOptions({
                                                        page: 1,
                                                        totalSize: response.data.totalCount,
                                                        sizePerPage: paginationOptions.sizePerPage
                                                    });
                                                });
                                            }}
                                            // disabled={formik.isSubmitting}
                                            className="btn btn-primary font-weight-bold px-4 mx-4"
                                            >
                                                <span>Clear</span>
                                        </button>
                                    </div>
                                </form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>

            {/* Category Select Component */}

            <div className="row">


                {typeof(products) !== 'undefined' ? products.map((product,index) => (
                    <ProductBox key={product.name + '-' + index} product={product} company={company} supplier={supplier} locations={locations} setShowSnackBar={setShowSnackBar}
                                getSupplierProductsNameBrand={getSupplierProductsNameBrand}  setProducts={setProducts} setPaginationOptions={setPaginationOptions}
                                searchBrand={searchBrand} searchCategory={searchCategory} paginationOptions={paginationOptions}
                                isOrderSummary={isOrderSummary} setIsCartUpdated={setIsCartUpdated} isCartUpdated={isCartUpdated} orderId={orderId} currentOrders={currentOrders}
                                setCurrentOrders={setCurrentOrders} defaultInvoiceDate={defaultInvoiceDate} defaultShipDate={defaultShipDate} order={order}
                                />
                ))
                    : null
                }
            </div>
            <div className="row justify-content-center">
                <div className="col-12">
                    <FormControl variant="filled" style={{ width: "100%" }}>
                        <Select
                            MenuProps={{ autoFocus: false }}
                            value={paginationOptions.sizePerPage}
                            style={{ minWidth: "200px" }}
                            input={<FilledInput name="pageSize" id="pageSize"
                                onChange={(element) => {
                                    handlePageSizeChange(element.target.value);
                                } }
                                type="text"
                                className="form-control form-control-solid h-auto" 
                            />}
                        >
                            <MenuItem key={"pageSize12"} value={12}>{"12"}</MenuItem>
                            <MenuItem key={"pageSize24"} value={24}>{"24"}</MenuItem>
                            <MenuItem key={"pageSize60"} value={60}>{"60"}</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                <Pagination
                    itemClass="page-item"
                    linkClass="page-link"
                    activePage={paginationOptions.page}
                    itemsCountPerPage={paginationOptions.sizePerPage}
                    totalItemsCount={paginationOptions.totalSize}
                    pageRangeDisplayed={5}
                    onChange={handlePageChange.bind(this)}
                />
            </div>
            {/* <SimpleSnackbar
                showSnackBar={showSnackBar}
                setShowSnackBar={setShowSnackBar}
            /> */}
            </div>
            </div>

    )
}
